/**
 * Языки интерфейса и выбор языка (ADR-0019).
 *
 * Код языка — BCP 47 с явной письменностью: узбекский пишется двумя
 * алфавитами, и «uz» без письменности не говорит, какой из них показывать.
 * Те же коды получает `Intl` (числа, даты, правила множественного числа).
 * Серверу уходит тот же выбор в форме Django (`uz-latn`, `uz-cyrl`) — через
 * cookie `django_language`, которую читает `LocaleMiddleware`.
 */

export const LOCALES = ['ru', 'uz-Latn', 'uz-Cyrl'] as const;
export type Locale = (typeof LOCALES)[number];

/** Язык-источник: на нём пишутся тексты, к нему откатываются пропуски перевода. */
export const SOURCE_LOCALE: Locale = 'ru';

/**
 * Самоназвания языков. Это НЕ переводимые строки: в переключателе язык
 * называется так, как его зовут его носители, на любом языке интерфейса.
 */
export const LOCALE_NAMES: Record<Locale, string> = {
  ru: 'Русский',
  'uz-Latn': 'Oʻzbekcha',
  'uz-Cyrl': 'Ўзбекча',
};

/** Короткая метка для узкой кнопки. */
export const LOCALE_SHORT: Record<Locale, string> = {
  ru: 'RU',
  'uz-Latn': 'UZ',
  'uz-Cyrl': 'ЎЗ',
};

/** Код языка для Django (`LANGUAGES` в settings). */
export const SERVER_LANGUAGE: Record<Locale, string> = {
  ru: 'ru',
  'uz-Latn': 'uz-latn',
  'uz-Cyrl': 'uz-cyrl',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Язык из тега браузера/сервера. Узбекский без письменности — латиница: это
 * государственный алфавит (закон 1993/1995); кириллица выбирается только явно
 * (`uz-Cyrl`). Неизвестный язык — `null`, решает следующий источник.
 */
export function matchLocale(tag: string | null | undefined): Locale | null {
  if (!tag) return null;
  const lower = tag.trim().toLowerCase().replace(/_/g, '-');
  if (!lower) return null;
  const [lang, ...rest] = lower.split('-');
  if (lang === 'ru') return 'ru';
  if (lang === 'uz') return rest.includes('cyrl') ? 'uz-Cyrl' : 'uz-Latn';
  return null;
}

/**
 * Языки, доступные в переключателе: по умолчанию — все три, и в разработке, и
 * в сборке (переводы uz-Latn/uz-Cyrl есть, журнал №1004 → №1112). Сузить
 * стенд можно `VITE_I18N_LOCALES` (через запятую), например `ru` — откат без
 * правки кода.
 */
export function enabledLocales(
  env: { DEV?: boolean; VITE_I18N_LOCALES?: string } = import.meta.env,
): Locale[] {
  const listed = (env.VITE_I18N_LOCALES ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(isLocale);
  if (listed.length) return LOCALES.filter((locale) => listed.includes(locale));
  return [...LOCALES];
}

/* ---------------------------------------------------------------- хранение */

export const STORAGE_KEY = 'agroai.locale';
/** Имя cookie, которую читает Django `LocaleMiddleware` (`LANGUAGE_COOKIE_NAME`). */
export const SERVER_COOKIE = 'django_language';
const YEAR_SECONDS = 365 * 24 * 60 * 60;

/** Сохранённый выбор человека или `null`. Хранилище может быть недоступно. */
export function readStoredLocale(): Locale | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Запомнить язык: в браузере (интерфейс) и в cookie (сервер). Cookie
 * ставится и тогда, когда язык выбран не человеком, а по браузеру: сервер и
 * интерфейс обязаны говорить на одном языке, а заголовок `Accept-Language`
 * браузера может с выбором интерфейса не совпадать.
 */
export function storeLocale(locale: Locale, { explicit }: { explicit: boolean }): void {
  try {
    if (explicit) window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Приватный режим: выбор проживёт до перезагрузки — это не ошибка экрана.
  }
  try {
    document.cookie = `${SERVER_COOKIE}=${SERVER_LANGUAGE[locale]}; path=/; max-age=${YEAR_SECONDS}; SameSite=Lax`;
  } catch {
    // Без cookie сервер ответит по Accept-Language — тексты API всё равно
    // переводятся на клиенте по коду.
  }
}

/** Начальный язык: сохранённый выбор → языки браузера → источник. */
export function initialLocale(enabled: readonly Locale[] = enabledLocales()): Locale {
  const stored = readStoredLocale();
  if (stored && enabled.includes(stored)) return stored;
  const browser = typeof navigator === 'undefined' ? [] : navigator.languages ?? [navigator.language];
  for (const tag of browser) {
    const locale = matchLocale(tag);
    if (locale && enabled.includes(locale)) return locale;
  }
  return enabled.includes(SOURCE_LOCALE) ? SOURCE_LOCALE : (enabled[0] ?? SOURCE_LOCALE);
}
